// Summary card shown at the top of the Captain's / Pilot's logbook pages.
// Renders the total hours plus a per-year breakdown of entries, hours,
// distance and trips/flights. Purely presentational: it takes a `summary`
// object produced by `summarizeLogbook` (see src/fleet-shared/LogbookTypes.mjs).
//
// The columns mirror LogbookTripsTable's (Entries | Hours | Distance), because
// a year row and the per-year page it opens are answering the same question and
// must not be shaped differently on the way there.
//
// `loading` renders a skeleton in the same card, at roughly the same height,
// rather than rendering nothing. The summary needs a scan of the WHOLE logbook
// -- separate from, and slower than, the paginated table below it -- so
// without this the table paints first and the card drops in on top of it,
// shoving the rows the user was already reading down the page.
//
// One component with a flag rather than two, so the card chrome, the header
// text and the page position can't drift between the two states.

import Table from 'react-bootstrap/Table';
import Button from 'react-bootstrap/Button';
import DataCard from '../../components/DataCard';
// The card is a whole-log overview, so it writes whole hours and whole miles
// (displayWholeHours / displayWholeNm). The per-year page and the trips table
// it sits beside keep displayHours / displayNm's one decimal: a decimal means
// something for a single passage and nothing for a season, so a year row and
// the page it opens deliberately round differently. displayWholeNm yields ''
// for an unmeasured year -- a blank cell, not "0 nm".
import { displayWholeHours as formatHours, displayWholeNm as displayNm } from '../../fleet-shared/LogbookTrips.mjs';
import { useIsMdUp } from '../../utilities/useIsMdUp';

// Below `md` the Distance column is headed `nm`, the legs table's rule (README
// § "Every row-level control on those tables is sized for a phone"). This
// column is sized by its HEADING, not its data: at 375px `Distance` takes 84px
// over cells holding `486.2 nm`, and that word is what pushed a five-column
// table to 329px inside a 309px card -- scrolling the Trips column half off the
// screen by default. Headed `nm` the table is 299px and fits, and `nm` is the
// more useful of the two labels anyway (the number was never dimensionless).
//
// Measured, not reasoned -- the README is explicit that the arithmetic which
// said the legs table already fitted was wrong by 32px. A logbook with a
// four-figure year still overflows by 10px, because there the CELL binds rather
// than the heading; that is the trips table's ordinary behaviour beside it
// (223px over the same card) and not worth a second formatter to chase.
const DISTANCE_HEADING = { wide: 'Distance', narrow: 'nm' };

const SUMMARY_HEADER = <h5 className="mb-0 fw-semibold text-center">Summary</h5>;

// Three placeholder rows: enough to hold the card's height for a typical
// logbook without pretending to know how many years it covers.
const SKELETON_ROWS = [0, 1, 2];

// Announced once, as a whole. The bars are decoration -- a screen reader
// reading out six grey blocks says nothing a single "Loading" doesn't.
const LoadingSummary = () => (
  <DataCard
    header={SUMMARY_HEADER}
    body={
      <div role="status" className="logbook-summary-skeleton">
        <span className="visually-hidden">Loading summary…</span>
        <span className="tp-skeleton logbook-summary-skeleton__total mb-3" aria-hidden="true" />
        <span className="tp-skeleton logbook-summary-skeleton__head" aria-hidden="true" />
        {SKELETON_ROWS.map((row) => (
          <span key={row} className="tp-skeleton logbook-summary-skeleton__row" aria-hidden="true" />
        ))}
      </div>
    }
  />
);

// One year row. Clickable only when the caller gave it somewhere to go -- the
// card stays presentational and knows nothing about trips: a logbook type with
// no per-year page simply passes no callback and the rows are plain text.
// `onSelectYear` arrives already resolved per row (null for a year the caller
// has nowhere to open), so this row asks no second question.
//
// Whole row plus a link on the year, the same double affordance the logbook
// list's trip-name cell uses: the row is the target, the link is what makes it
// discoverable and reachable from a keyboard. stopPropagation so one click
// navigates once.
const YearRow = ({ row, showEntries, showDistance, onSelectYear }) => (
  <tr
    {...(onSelectYear && {
      onClick: () => onSelectYear(row.year),
      role: 'button',
      style: { cursor: 'pointer' },
    })}
  >
    <td>
      {onSelectYear ? (
        <Button
          variant="link"
          className="p-0 align-baseline text-start"
          onClick={(e) => { e.stopPropagation(); onSelectYear(row.year); }}
        >
          {row.year}
        </Button>
      ) : row.year}
    </td>
    {showEntries && <td className="text-end">{row.entryCount}</td>}
    <td className="text-end">{formatHours(row.hours)}</td>
    {showDistance && <td className="text-end">{displayNm(row.distanceNm)}</td>}
    <td className="text-end">{row.count}</td>
  </tr>
);

// `canSelectYear(year)` is an optional per-row gate on `onSelectYear`: a row
// it answers false for is plain text while its siblings stay clickable. The
// fleet Trips page needs it -- a fleet summary can hold a year whose entries
// are all a Pilot's flights, and that year has no trip page anywhere to open,
// so a click would land on some other boat's empty year. Absent, every year is
// selectable, which is what the logbook page (one log, one type) has always
// had.
export const LogbookSummaryCard = ({ summary, loading, onSelectYear, canSelectYear }) => {
  // The table owns its own breakpoint, TaskTable's pattern -- one heading word
  // changes, not the column set, so there is nothing for the page to decide.
  const isMdUp = useIsMdUp();
  // Loading wins: a caller with a stale summary and a fresh fetch in flight
  // means the numbers on screen are about to change.
  if (loading) return <LoadingSummary />;
  if (!summary) return null;
  const {
    totalHours, totalEntries, totalDistanceNm, totalCount, countLabel, byYear,
  } = summary;

  // A type whose count IS its entry count (the Pilot's Logbook counts rows, and
  // calls them Flights) gets no Entries column: it would print the same number
  // twice under a worse name. Asked of the summary, not of a type id, and not
  // of whether the two numbers happen to differ -- a year with one entry in one
  // trip would defeat that.
  const showEntries = !summary.countIsEntries;

  // A logbook whose entries carry no routes AT ALL grows no Distance column --
  // otherwise it is a heading over a column of blanks for the life of the
  // logbook, which on a phone is a fifth of the card's width spent saying
  // nothing. The gate is the whole logbook's total, not the year's: a year that
  // measured nothing still gets its blank cell (that is `displayNm(null)`, and
  // the years around it have numbers to line up with). The column appears the
  // first time any entry gets a route, which is the right moment for it.
  const showDistance = totalDistanceNm != null;

  return (
    <DataCard
      header={SUMMARY_HEADER}
      body={
        <>
          <p className="text-center fs-4 mb-3">
            <span className="text-body-secondary me-2">Total Hours:</span>
            <span className="fw-semibold">{formatHours(totalHours)}</span>
          </p>
          <Table responsive hover className="align-middle mb-0">
            <thead className="bg-body-tertiary">
              <tr>
                <th scope="col">Year</th>
                {showEntries && <th scope="col" className="text-end">Entries</th>}
                <th scope="col" className="text-end">Hours</th>
                {showDistance && (
                  <th scope="col" className="text-end">
                    {isMdUp ? DISTANCE_HEADING.wide : DISTANCE_HEADING.narrow}
                  </th>
                )}
                <th scope="col" className="text-end">{countLabel}</th>
              </tr>
            </thead>
            <tbody>
              {byYear.map((row) => (
                <YearRow
                  key={row.year}
                  row={row}
                  showEntries={showEntries}
                  showDistance={showDistance}
                  onSelectYear={
                    onSelectYear && (!canSelectYear || canSelectYear(row.year))
                      ? onSelectYear
                      : undefined
                  }
                />
              ))}
            </tbody>
            {/* These totals cover every entry, including one whose date puts it
                in no year at all, so a column can add up to less than its own
                footer. That is summarizeLogbook's rule 4, not a rounding bug. */}
            <tfoot>
              <tr className="fw-semibold border-top">
                <td>Total</td>
                {showEntries && <td className="text-end">{totalEntries}</td>}
                <td className="text-end">{formatHours(totalHours)}</td>
                {showDistance && <td className="text-end">{displayNm(totalDistanceNm)}</td>}
                <td className="text-end">{totalCount}</td>
              </tr>
            </tfoot>
          </Table>
        </>
      }
    />
  );
};

export default LogbookSummaryCard;
